
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 * Landing Page - Marketing homepage for Compliance Command
*/
import React from 'react';

interface LandingPageProps {
  onEnter: () => void;
  onNavigateToMission: () => void;
  onNavigateToHowItWorks: () => void;
}

const LandingPage: React.FC<LandingPageProps> = ({ onEnter, onNavigateToMission, onNavigateToHowItWorks }) => {
  // Industry categories for showcase
  const industries = [
    { 
      name: 'FinTech', 
      icon: '💳', 
      color: '#10B981', 
      desc: 'Digital banking, payments, crypto',
      frameworks: ['PCI-DSS', 'AML/KYC', 'SOX']
    },
    { 
      name: 'HealthTech', 
      icon: '🏥', 
      color: '#3B82F6', 
      desc: 'Patient data, telehealth, FDA',
      frameworks: ['HIPAA', 'HITECH', 'FDA']
    },
    { 
      name: 'B2B SaaS', 
      icon: '💼', 
      color: '#8B5CF6', 
      desc: 'Enterprise software, cloud security',
      frameworks: ['SOC 2', 'ISO 27001', 'GDPR']
    },
  ];

  const stats = [
    { value: '12+', label: 'Industry Scenarios' },
    { value: '50+', label: 'Building Types' },
    { value: '7', label: 'Regulatory Frameworks' },
    { value: '∞', label: 'Learning Paths' },
  ];

  return (
    <div className="min-h-dvh bg-[#050505] text-white font-sans selection:bg-emerald-500/30">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-black/70 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-cyan-600 flex items-center justify-center text-white font-black text-xl shadow-[0_0_20px_rgba(16,185,129,0.4)]">
              C
            </div>
            <div>
              <span className="font-bold text-lg tracking-tight text-white">Compliance Command</span>
              <span className="hidden sm:inline text-xs text-slate-500 ml-2">by ZeroTrust</span>
            </div>
          </div>
          
          <div className="hidden md:flex items-center gap-8 text-sm font-medium text-slate-400">
            <a href="#industries" className="hover:text-white transition-colors">Industries</a>
            <a href="#features" className="hover:text-white transition-colors">Features</a>
            <button onClick={onNavigateToMission} className="hover:text-white transition-colors">About</button>
            <button onClick={onNavigateToHowItWorks} className="hover:text-white transition-colors">How It Works</button>
          </div>

          <button 
            onClick={onEnter}
            data-testid="landing-enter"
            className="bg-gradient-to-r from-emerald-500 to-cyan-500 text-black px-6 py-2.5 rounded-full font-bold text-sm hover:shadow-[0_0_30px_rgba(16,185,129,0.4)] transition-all"
          >
            Play Now
          </button>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-24 px-6 relative overflow-hidden min-h-screen flex items-center">
        {/* Animated Background */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/4 left-1/4 w-[600px] h-[600px] bg-emerald-600/20 rounded-full blur-[150px] animate-pulse"></div>
          <div className="absolute bottom-1/4 right-1/4 w-[500px] h-[500px] bg-cyan-600/15 rounded-full blur-[120px] animate-pulse" style={{ animationDelay: '1s' }}></div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-indigo-600/10 rounded-full blur-[180px]"></div>
          {/* Grid pattern */}
          <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[length:60px_60px]"></div>
        </div>

        <div className="max-w-7xl mx-auto relative z-10 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-950/50 border border-emerald-500/30 text-emerald-300 text-xs font-bold uppercase tracking-widest mb-8">
                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></span>
                Interactive Compliance Training
              </div>
              
              <h1 className="text-5xl md:text-7xl font-black tracking-tighter leading-[1.05] mb-6">
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-white via-slate-200 to-slate-400">
                  Master Compliance
                </span>
                <br/>
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-emerald-400 via-cyan-400 to-blue-400">
                  Through Simulation
                </span>
              </h1>
              
              <p className="text-xl text-slate-400 leading-relaxed mb-10 max-w-xl">
                Build, manage, and defend your compliance infrastructure in a real-time city simulation. 
                Learn PCI-DSS, HIPAA, SOC 2, and more through hands-on experience—not boring slideshows.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 mb-12">
                <button 
                  onClick={onEnter}
                  data-testid="landing-enter"
                  className="px-10 py-4 bg-gradient-to-r from-emerald-600 to-cyan-600 hover:from-emerald-500 hover:to-cyan-500 text-white font-bold rounded-full transition-all shadow-[0_0_40px_rgba(16,185,129,0.3)] text-lg flex items-center justify-center gap-3 group"
                >
                  Start Building
                  <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </button>
                <button 
                  onClick={onNavigateToHowItWorks}
                  className="px-10 py-4 bg-white/5 border border-white/20 hover:bg-white/10 hover:border-white/30 text-white font-bold rounded-full transition-all text-lg flex items-center justify-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Watch Demo
                </button>
              </div>

              {/* Stats Row */}
              <div className="grid grid-cols-4 gap-4">
                {stats.map((stat, idx) => (
                  <div key={idx} className="text-center">
                    <div className="text-2xl md:text-3xl font-black text-white">{stat.value}</div>
                    <div className="text-xs text-slate-500 uppercase tracking-wider">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Hero Visual */}
            <div className="relative hidden lg:block">
              <div className="relative w-full aspect-square max-w-[550px] mx-auto">
                {/* Glowing orb */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-gradient-to-br from-emerald-500/30 to-cyan-500/30 rounded-full blur-[60px] animate-pulse"></div>
                
                {/* Floating UI Cards */}
                <div className="absolute top-[10%] right-[5%] bg-slate-900/95 border border-emerald-500/30 p-4 rounded-xl shadow-2xl animate-[float_4s_ease-in-out_infinite] backdrop-blur-sm">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center text-emerald-400 font-bold">✓</div>
                    <div>
                      <div className="text-xs text-slate-400 uppercase font-bold">Compliance</div>
                      <div className="text-xl font-mono font-bold text-emerald-400">94%</div>
                    </div>
                  </div>
                </div>

                <div className="absolute top-[35%] left-[0%] bg-slate-900/95 border border-cyan-500/30 p-4 rounded-xl shadow-2xl animate-[float_5s_ease-in-out_infinite_reverse] backdrop-blur-sm">
                  <div className="text-xs text-slate-400 uppercase font-bold mb-1">Budget</div>
                  <div className="text-2xl font-mono font-bold text-cyan-400">$1.2M</div>
                </div>

                <div className="absolute bottom-[15%] right-[10%] bg-slate-900/95 border border-red-500/30 p-4 rounded-xl shadow-2xl animate-[float_6s_ease-in-out_infinite] backdrop-blur-sm">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-red-500/20 flex items-center justify-center text-xl">🛡️</div>
                    <div>
                      <div className="text-xs text-slate-400 uppercase font-bold">Threat Level</div>
                      <div className="text-sm font-bold text-red-400">LOW</div>
                    </div>
                  </div>
                </div>

                <div className="absolute bottom-[35%] left-[15%] bg-slate-900/95 border border-indigo-500/30 p-4 rounded-xl shadow-2xl animate-[float_4.5s_ease-in-out_infinite] backdrop-blur-sm">
                  <div className="text-xs text-slate-400 uppercase font-bold mb-2">Active Products</div>
                  <div className="flex gap-2">
                    <span className="px-2 py-1 bg-indigo-500/20 rounded text-xs text-indigo-300">Checking</span>
                    <span className="px-2 py-1 bg-purple-500/20 rounded text-xs text-purple-300">Crypto</span>
                  </div>
                </div>

                {/* Center Icon */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-24 h-24 bg-gradient-to-br from-emerald-600 to-cyan-600 rounded-2xl flex items-center justify-center shadow-[0_0_60px_rgba(16,185,129,0.4)] rotate-12">
                  <span className="text-4xl -rotate-12">🏛️</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Industry Categories Section */}
      <section id="industries" className="py-24 px-6 bg-gradient-to-b from-black to-slate-950 relative">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-950/50 border border-indigo-500/30 text-indigo-300 text-xs font-bold uppercase tracking-widest mb-6">
              Choose Your Path
            </div>
            <h2 className="text-4xl md:text-5xl font-black text-white mb-4">Three Industry Verticals</h2>
            <p className="text-xl text-slate-400 max-w-2xl mx-auto">
              Each industry has unique regulatory challenges, products, and scenarios. 
              Master one or conquer them all.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {industries.map((industry, idx) => (
              <div 
                key={idx}
                className="group relative bg-slate-900/50 border border-white/10 rounded-2xl p-8 hover:border-opacity-50 transition-all duration-500 overflow-hidden"
                style={{ '--accent': industry.color } as React.CSSProperties}
              >
                {/* Hover glow */}
                <div 
                  className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                  style={{ background: `radial-gradient(circle at 50% 100%, ${industry.color}15, transparent 70%)` }}
                />
                
                <div className="relative z-10">
                  <div 
                    className="w-16 h-16 rounded-2xl flex items-center justify-center text-3xl mb-6 transition-transform group-hover:scale-110"
                    style={{ backgroundColor: `${industry.color}20` }}
                  >
                    {industry.icon}
                  </div>
                  
                  <h3 className="text-2xl font-bold text-white mb-2">{industry.name}</h3>
                  <p className="text-slate-400 mb-6">{industry.desc}</p>
                  
                  <div className="flex flex-wrap gap-2">
                    {industry.frameworks.map((fw, fwIdx) => (
                      <span 
                        key={fwIdx}
                        className="px-3 py-1 rounded-lg text-xs font-bold"
                        style={{ backgroundColor: `${industry.color}20`, color: industry.color }}
                      >
                        {fw}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Sandbox callout */}
          <div className="mt-12 text-center">
            <div className="inline-flex items-center gap-3 px-6 py-3 bg-amber-950/30 border border-amber-500/30 rounded-full">
              <span className="text-2xl">🎮</span>
              <span className="text-amber-300 font-medium">Or play <strong>Sandbox Mode</strong> with all components unlocked</span>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 px-6 relative">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-black text-white mb-4">Why Compliance Command?</h2>
            <p className="text-xl text-slate-400 max-w-2xl mx-auto">
              Traditional compliance training is broken. We fixed it.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-gradient-to-br from-emerald-950/30 to-teal-950/30 border border-emerald-500/20 rounded-2xl p-8">
              <div className="w-14 h-14 rounded-xl bg-emerald-500/20 flex items-center justify-center text-2xl mb-6">
                🏗️
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Build Real Infrastructure</h3>
              <p className="text-slate-400">
                Place firewalls, compliance centers, and security operations. See how they connect and protect your organization.
              </p>
            </div>

            <div className="bg-gradient-to-br from-cyan-950/30 to-blue-950/30 border border-cyan-500/20 rounded-2xl p-8">
              <div className="w-14 h-14 rounded-xl bg-cyan-500/20 flex items-center justify-center text-2xl mb-6">
                ⚡
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Real-Time Simulation</h3>
              <p className="text-slate-400">
                Watch compliance decay, threats propagate, and budgets fluctuate. Every decision has immediate consequences.
              </p>
            </div>

            <div className="bg-gradient-to-br from-indigo-950/30 to-purple-950/30 border border-indigo-500/20 rounded-2xl p-8">
              <div className="w-14 h-14 rounded-xl bg-indigo-500/20 flex items-center justify-center text-2xl mb-6">
                🎯
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Scenario-Based Learning</h3>
              <p className="text-slate-400">
                Face real-world crises: data breaches, regulatory audits, market crashes. Learn by doing, not reading.
              </p>
            </div>

            <div className="bg-gradient-to-br from-purple-950/30 to-pink-950/30 border border-purple-500/20 rounded-2xl p-8">
              <div className="w-14 h-14 rounded-xl bg-purple-500/20 flex items-center justify-center text-2xl mb-6">
                📊
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Comprehensive Dashboards</h3>
              <p className="text-slate-400">
                Track compliance metrics, financial health, security posture, and organizational structure in beautiful dashboards.
              </p>
            </div>

            <div className="bg-gradient-to-br from-rose-950/30 to-orange-950/30 border border-rose-500/20 rounded-2xl p-8">
              <div className="w-14 h-14 rounded-xl bg-rose-500/20 flex items-center justify-center text-2xl mb-6">
                🛡️
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Risk Management</h3>
              <p className="text-slate-400">
                Balance growth with security. Launch products, manage vendors, and respond to incidents—all while staying compliant.
              </p>
            </div>

            <div className="bg-gradient-to-br from-amber-950/30 to-yellow-950/30 border border-amber-500/20 rounded-2xl p-8">
              <div className="w-14 h-14 rounded-xl bg-amber-500/20 flex items-center justify-center text-2xl mb-6">
                🎓
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Progressive Learning</h3>
              <p className="text-slate-400">
                Start with tutorials, advance to complex scenarios. Unlock new buildings and capabilities as you master concepts.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Social Proof / Trust Section */}
      <section className="py-24 px-6 bg-black">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            <div className="p-8 bg-white/5 rounded-2xl border border-white/5">
              <div className="text-4xl mb-4">🛡️</div>
              <h3 className="font-bold text-xl text-white mb-3">Privacy First</h3>
              <p className="text-slate-400">Your simulation data lives in your browser. We don't harvest your strategies or sell your data.</p>
            </div>
            <div className="p-8 bg-white/5 rounded-2xl border border-white/5">
              <div className="text-4xl mb-4">⚡</div>
              <h3 className="font-bold text-xl text-white mb-3">60fps Performance</h3>
              <p className="text-slate-400">Built with React + Three.js for smooth, responsive gameplay on any modern device.</p>
            </div>
            <div className="p-8 bg-white/5 rounded-2xl border border-white/5">
              <div className="text-4xl mb-4">🆓</div>
              <h3 className="font-bold text-xl text-white mb-3">100% Free</h3>
              <p className="text-slate-400">No ads, no microtransactions, no premium currency. Just pure learning through simulation.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-emerald-600/10 via-cyan-600/10 to-indigo-600/10"></div>
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-black text-white mb-6">Ready to Build Your Compliance Empire?</h2>
          <p className="text-xl text-slate-400 mb-10 max-w-2xl mx-auto">
            Join thousands of professionals learning compliance the engaging way. No signup required—start playing instantly.
          </p>
          <button 
            onClick={onEnter}
            data-testid="landing-enter"
            className="px-12 py-5 bg-gradient-to-r from-emerald-600 to-cyan-600 hover:from-emerald-500 hover:to-cyan-500 text-white font-bold rounded-full transition-all shadow-[0_0_50px_rgba(16,185,129,0.4)] text-xl"
          >
            Launch Compliance Command →
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t border-white/10 bg-black">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500 to-cyan-600 flex items-center justify-center font-bold text-sm">C</div>
              <span className="font-bold text-white">Compliance Command</span>
            </div>
            <div className="flex items-center gap-6 text-sm text-slate-500">
              <button onClick={onNavigateToMission} className="hover:text-white transition-colors">About</button>
              <button onClick={onNavigateToHowItWorks} className="hover:text-white transition-colors">How It Works</button>
              <span>•</span>
              <span>© 2025 ZeroTrust. All rights reserved.</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
